import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from '../ui/badge';
import { CreditCard, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { STRIPE_CONFIG } from '../lib/config';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: (wasSuccessful?: boolean) => void;
  projectId: string;
  paymentIntentId: string;
  paymentIntentClientSecret: string;
  costEstimate: {
    totalPages: number;
    processedPages: number;
    costCents: number;
    costDollars: number;
    description: string;
  };
  billingBreakdown: {
    canProcess: boolean;
    trialPagesUsed: number;
    paidPagesNeeded: number;
    costCents: number;
    costDollars: number;
    hasMinimumCharge?: boolean;
  };
  onPaymentSuccess: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  projectId,
  paymentIntentId,
  paymentIntentClientSecret,
  costEstimate,
  billingBreakdown,
  onPaymentSuccess
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripe, setStripe] = useState<any>(null);
  const [elements, setElements] = useState<any>(null);
  const [cardElement, setCardElement] = useState<any>(null);
  
  // Payment Element handles billing details automatically

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessing) {
        console.log('💳 [PAYMENT MODAL] Escape key pressed - closing modal');
        onClose(false); // Pass false to indicate canceled/dismissed
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isProcessing, onClose]);

  useEffect(() => {
    // Load and initialize Stripe
    const loadStripe = async () => {
      if (!window.Stripe) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.onload = () => initializeStripe();
        document.head.appendChild(script);
      } else {
        initializeStripe();
      }
    };

    const initializeStripe = () => {
      console.log('🔧 [STRIPE] Initializing with key:', STRIPE_CONFIG.publishableKey?.substring(0, 12) + '...');
      
      if (!STRIPE_CONFIG.publishableKey || STRIPE_CONFIG.publishableKey.includes('your_publishable_key_here')) {
        console.error('❌ [STRIPE] Invalid publishable key:', STRIPE_CONFIG.publishableKey);
        setError('Stripe configuration error. Please check environment variables.');
        return;
      }
      
      try {
        const stripeInstance = window.Stripe(STRIPE_CONFIG.publishableKey);
        const elementsInstance = stripeInstance.elements({
          clientSecret: paymentIntentClientSecret,
          appearance: {
            theme: 'night', // Dark theme to match our UI
            variables: {
              colorPrimary: '#f97316', // Orange color to match our theme
              colorBackground: '#1f2937',
              colorText: '#f3f4f6',
              colorDanger: '#ef4444',
              borderRadius: '8px'
            }
          }
        });

        // Use Payment Element - payment methods restricted at Payment Intent level
        const paymentElementInstance = elementsInstance.create('payment', {
          fields: {
            billingDetails: 'auto' // Let Stripe determine what's needed automatically
          },
          wallets: {
            applePay: 'never',
            googlePay: 'never'
          },
          terms: {
            card: 'never' // Disable "Save for future payments" checkbox
          }
        });

        setStripe(stripeInstance);
        setElements(elementsInstance);
        setCardElement(paymentElementInstance); // Reusing the same state variable
        console.log('✅ [STRIPE] Payment Element initialized successfully');
        console.log('💳 [STRIPE] Configuration:', {
          paymentMethodsRestrictedAt: 'PaymentIntent level',
          billingDetails: 'auto',
          wallets: 'disabled',
          terms: 'disabled'
        });
      } catch (err) {
        console.error('❌ [STRIPE] Initialization failed:', err);
        setError('Failed to initialize payment system. Please try again.');
      }
    };

    if (isOpen) {
      loadStripe();
    }
  }, [isOpen]);

  useEffect(() => {
    // Mount card element when available
    if (cardElement && isOpen) {
      const cardContainer = document.getElementById('card-element');
      if (cardContainer) {
        console.log('💳 [STRIPE] Mounting card element to container');
        try {
          if (!cardContainer.hasChildNodes()) {
            cardElement.mount('#card-element');
            console.log('✅ [STRIPE] Payment element mounted successfully');
          } else {
            console.log('💳 [STRIPE] Payment element already mounted');
          }
        } catch (error) {
          console.error('❌ [STRIPE] Failed to mount payment element:', error);
          setError('Failed to initialize payment form. Please try again.');
        }
      } else {
        console.error('❌ [STRIPE] Card container not found');
        setError('Payment form container not found. Please try again.');
      }
    }

    // Cleanup on unmount
    return () => {
      if (cardElement) {
        try {
          cardElement.unmount();
          console.log('🧹 [STRIPE] Card element unmounted');
        } catch (error) {
          console.warn('⚠️ [STRIPE] Error unmounting card element:', error);
        }
      }
    };
  }, [cardElement, isOpen]);

  const handlePayment = async () => {
    if (!stripe || !cardElement || !paymentIntentClientSecret) {
      console.error('❌ [PAYMENT] Missing requirements:', { 
        stripe: !!stripe, 
        cardElement: !!cardElement, 
        paymentIntentClientSecret: !!paymentIntentClientSecret 
      });
      setError('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      console.log('💳 [STRIPE] Confirming payment with client secret:', paymentIntentClientSecret?.substring(0, 20) + '...');
      console.log('💳 [STRIPE] Using publishable key:', STRIPE_CONFIG.publishableKey?.substring(0, 12) + '...');
      
      // Confirm payment with Stripe using Payment Element
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin, // Required but not used since we handle confirmation here
          payment_method_data: {
            billing_details: {
              // Provide default values for any fields we might have set to 'never'
              // This prevents the billing details error
            }
          }
        },
        redirect: 'if_required' // Don't redirect, handle confirmation in our code
      });

      if (stripeError) {
        console.error('❌ [STRIPE] Payment failed:', stripeError);
        
        // Handle specific test mode error with helpful message
        if (stripeError.message?.includes('test mode') && stripeError.message?.includes('non test card')) {
          setError('Please use a test card number like 4242 4242 4242 4242 since this system is in test mode.');
        } else {
          setError(stripeError.message || 'Payment failed');
        }
        return;
      }

      console.log('💳 [STRIPE] Payment intent result:', paymentIntent);

      if (paymentIntent.status === 'succeeded') {
        console.log('✅ [STRIPE] Payment succeeded, confirming with backend');
        
        // Confirm with our backend
        const { getApiUrl } = await import('../lib/config');
        const token = localStorage.getItem('orca_auth_token');
        const apiUrl = getApiUrl(`/api/projects/${projectId}/confirm-payment-and-start`);
        
        console.log('💳 [BACKEND] Calling confirmation API:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ paymentIntentId })
        });

        console.log('💳 [BACKEND] Response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          console.log('✅ [BACKEND] Payment confirmed and workflow started:', result);
          onPaymentSuccess();
          onClose(true); // Pass true to indicate successful payment
        } else {
          const errorData = await response.json();
          console.error('❌ [BACKEND] Payment confirmation failed:', errorData);
          setError(errorData.error || `Payment confirmation failed (${response.status})`);
        }
      } else {
        console.error('❌ [STRIPE] Unexpected payment status:', paymentIntent.status);
        setError(`Payment ${paymentIntent.status}. Please try again.`);
      }
    } catch (err: any) {
      console.error('❌ [PAYMENT] Payment processing error:', err);
      setError(`Payment processing failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };


  if (!isOpen) return null;

  // Debug info
  console.log('🔍 [DEBUG] PaymentModal render:', {
    isOpen,
    projectId,
    paymentIntentId,
    paymentIntentClientSecret: paymentIntentClientSecret?.substring(0, 20) + '...',
    stripe: !!stripe,
    cardElement: !!cardElement,
    stripeConfig: STRIPE_CONFIG.publishableKey?.substring(0, 12) + '...'
  });

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Only close if clicking the backdrop, not the modal content
        if (e.target === e.currentTarget && !isProcessing) {
          console.log('💳 [PAYMENT MODAL] Backdrop clicked - closing modal');
          onClose(false); // Pass false to indicate canceled/dismissed
        }
      }}
    >
      <div 
        className="bg-card border border-border rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking modal content
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Payment Required</h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Cost Summary */}
          <div className="bg-muted/30 border border-border/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="text-orange-400" size={16} />
              <h3 className="text-sm font-medium text-foreground">Payment Summary</h3>
            </div>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex justify-between items-center">
                <span>Total pages to process:</span>
                <strong className="text-foreground">{costEstimate.processedPages}</strong>
              </div>
              
              {billingBreakdown.trialPagesUsed > 0 && (
                <div className="flex justify-between items-center">
                  <span>Free trial pages used:</span>
                  <strong className="text-blue-400">{billingBreakdown.trialPagesUsed}</strong>
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <span>Pages requiring payment:</span>
                <strong className="text-orange-400">{billingBreakdown.paidPagesNeeded}</strong>
              </div>
              
              <div className="flex justify-between items-center font-medium text-base pt-2 border-t border-border/50">
                <span>Total cost:</span>
                <strong className="text-foreground">${billingBreakdown.costDollars.toFixed(2)}</strong>
              </div>
              
              {billingBreakdown.hasMinimumCharge && (
                <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1">
                    <AlertCircle size={12} />
                    <span>$0.50 minimum charge applies (payment processor requirement)</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle size={16} />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Stripe Payment Form */}
          <div className="bg-muted/20 border border-border/50 rounded-lg p-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Payment Information
              </label>
              <div 
                id="card-element"
                className="border border-border rounded-md bg-card min-h-[120px]"
              >
                {/* Stripe Payment Element will mount here (includes card details + billing info) */}
              </div>
            </div>
            
          </div>

          {/* Test Mode Notice - only show if using test keys */}
          {STRIPE_CONFIG.publishableKey?.startsWith('pk_test_') && (
            <div className="text-xs text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded p-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={12} />
                <span className="font-medium">Test Mode Active</span>
              </div>
              <p className="mb-2">
                This system is in test mode. Use these test card numbers:
              </p>
              <div className="font-mono text-xs space-y-1">
                <div><strong>Card:</strong> 4242 4242 4242 4242</div>
                <div><strong>Expiry:</strong> Any future date</div>
                <div><strong>CVC:</strong> Any 3 digits</div>
                <div><strong>ZIP:</strong> Any 5 digits</div>
              </div>
            </div>
          )}

          {/* Payment Info */}
          <div className="text-xs text-muted-foreground bg-muted/20 rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard size={12} />
              <span className="font-medium">Secure Payment</span>
            </div>
            <p>
              Your payment information is processed securely by Stripe. 
              We never store your payment details on our servers.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayment}
            disabled={isProcessing || !stripe || !cardElement}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isProcessing ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Processing Payment...
              </>
            ) : (
              <>
                <CreditCard className="mr-2" size={16} />
                Pay ${billingBreakdown.costDollars.toFixed(2)}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
